import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Home, Briefcase, Search, MapPin } from "lucide-react";
import { useAuth } from "../lib/auth";

const HERO_IMG =
  "https://images.pexels.com/photos/21835254/pexels-photo-21835254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

export default function Hero({ content, logoUrl }) {
  const { isAuthed, requireAuth } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const gotoSearch = () => {
    const path = "/listings" + (search.trim() ? `?q=${encodeURIComponent(search.trim())}` : "");
    if (!isAuthed) {
      requireAuth({ redirectTo: path });
      return;
    }
    navigate(path);
  };

  const gotoGetStarted = () => {
    if (!isAuthed) {
      requireAuth({ redirectTo: "/listings" });
      return;
    }
    navigate("/listings");
  };

  const gotoRole = (role) => {
    const path = role === "homeowner" ? "/owner" : "/listings";
    if (!isAuthed) {
      requireAuth({ redirectTo: path });
      return;
    }
    navigate(path);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      gotoSearch();
    }
  };

  return (
    <section id="top" className="relative">
      <div
        className="relative h-[58vh] min-h-[440px] sm:h-[64vh] sm:min-h-[520px] bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      >
        <div className="absolute inset-0 bg-[rgba(30,63,51,0.6)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(30,63,51,0.4)] via-transparent to-[rgba(30,63,51,0.75)]" />

        <div className="container-z relative pt-6 sm:pt-8">
          <div className="flex items-center gap-3" data-testid="hero-brand">
            <span className="w-3 h-3 rounded-full bg-[#C86646]" />
            <img src={logoUrl} alt="Zumroo" className="h-11 w-11 rounded-lg" />
          </div>
        </div>

        <div className="container-z absolute inset-x-0 bottom-20 sm:bottom-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[#E9C46A]" />
              Live in Pune · Mumbai launching soon
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]"
              data-testid="hero-headline"
            >
              Pune's broker–owner<br />property marketplace.
            </h1>
            <p
              className="mt-5 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed"
              data-testid="hero-subtext"
            >
              {content.subtext}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Overlapping cream sheet */}
      <div className="hero-sheet">
        <div className="container-z py-10 sm:py-14">
          {/* Search bar — relative + low z-index so it slides under the fixed navbar (z-40) cleanly */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 -mt-20 sm:-mt-24 mb-10 sm:mb-14 px-2 sm:px-0"
          >
            <div className="zcard p-2 sm:p-2.5 flex items-center gap-2 max-w-3xl mx-auto shadow-xl">
              <div className="hidden sm:flex items-center pl-3 text-[#1E3F33]">
                <MapPin className="w-4 h-4" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search by locality, society or city (e.g. Kothrud, Baner)…"
                className="flex-1 px-3 sm:px-2 py-3 outline-none bg-transparent text-[#1A231E] placeholder:text-[#5C6B62] text-sm sm:text-base"
                data-testid="hero-search-input"
              />
              <button
                onClick={gotoSearch}
                className="btn-primary !py-3 !px-5 text-sm flex-shrink-0"
                data-testid="hero-search-btn"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>
            <div className="text-center text-xs text-[#5C6B62] mt-3">
              <span className="font-semibold text-[#1A231E]">Tip:</span> you'll need to sign in to view contact details. It's free.
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Role cards */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="label-mono mb-3">Get started</div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A231E]">
                Choose how you'll use Zumroo
              </h2>

              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={() => gotoRole("homeowner")}
                  className="w-full flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#E4E1D9] hover:border-[#1E3F33] transition group text-left"
                  data-testid="role-homeowner"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#EAF1ED] flex items-center justify-center text-[#1E3F33] group-hover:bg-[#1E3F33] group-hover:text-white transition">
                    <Home className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#1A231E]">I'm a Home Owner</div>
                    <div className="text-sm text-[#5C6B62]">List for free, get verified buyers</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#5C6B62] group-hover:text-[#C86646] transition" />
                </button>
                <button
                  type="button"
                  onClick={() => gotoRole("broker")}
                  className="w-full flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#E4E1D9] hover:border-[#1E3F33] transition group text-left"
                  data-testid="role-broker"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#FBEFE9] flex items-center justify-center text-[#C86646] group-hover:bg-[#C86646] group-hover:text-white transition">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#1A231E]">I'm a Broker</div>
                    <div className="text-sm text-[#5C6B62]">
                      Unlock owner contacts for {content.price_basic}/{content.price_premium}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#5C6B62] group-hover:text-[#C86646] transition" />
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={gotoGetStarted} className="btn-primary" data-testid="hero-get-started-btn">
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={gotoSearch} className="btn-outline" data-testid="hero-browse-btn">
                  <Search className="w-4 h-4" />
                  Browse Listings
                </button>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#5C6B62]">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-[#2D6A4F]" />
                  Verified owner numbers
                </div>
                <span className="w-1 h-1 rounded-full bg-[#E4E1D9]" />
                <span>Pay only when you unlock</span>
                <span className="w-1 h-1 rounded-full bg-[#E4E1D9]" />
                <span>Free for owners</span>
              </div>
            </motion.div>

            {/* Coming-soon highlight card replaces the QR card */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="lg:col-span-5"
            >
              <div className="zcard p-6 sm:p-7 sticky top-24 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#FBEFE9]" />
                <div className="absolute -bottom-12 -left-10 w-44 h-44 rounded-full bg-[#EAF1ED]" />
                <div className="relative">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C86646] text-white text-[10px] font-bold tracking-wider uppercase">
                    <Sparkles className="w-3 h-3" /> Coming soon
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1A231E] mt-4 leading-tight">
                    Mobile App<br /><span className="text-[#C86646]">Coming Soon!</span>
                  </h3>
                  <p className="mt-3 text-sm text-[#5C6B62] leading-relaxed">
                    The Zumroo Android &amp; iOS apps are in private beta. For now, browse and
                    sign in right here on the web — your account will carry over the day we launch.
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-xs font-semibold text-[#5C6B62]">
                    <span className="px-2.5 py-1 rounded-full bg-white border border-[#E4E1D9]">Android</span>
                    <span className="px-2.5 py-1 rounded-full bg-white border border-[#E4E1D9]">iOS</span>
                    <span className="text-[#1E3F33]">— launching together</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
